import Icon from "@/components/ui/icon";
import Popover from "@/components/ui/popover";
import { DeleteThemeDialog } from "@/dialogs/delete-theme-dialog";
import { RenameThemeDialog } from "@/dialogs/rename-theme-dialog";
import { cn } from "@/lib/cn";
import { useUpdateDefaultThemeMutation } from "@/shared/services";
import type { TDesignSystem } from "@/shared/types";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { IconEdit, IconStar, IconTrash } from "@tabler/icons-react";
import { useState } from "react";

const ThemeItemActions = ({
  theme,
  isDefault,
  closeThemesSelectPopover,
}: {
  theme: TDesignSystem;
  isDefault: boolean;
  closeThemesSelectPopover: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { mutate: updateDefaultTheme } = useUpdateDefaultThemeMutation();

  const handleRename = () => {
    setIsOpen(false);
    closeThemesSelectPopover();
    dispatch(
      openDialog({
        id: RenameThemeDialog.dialogId,
        content: RenameThemeDialog,
        props: { theme },
        closeOnOutsideClick: true,
      }),
    );
  };

  const handleMakeDefault = () => {
    updateDefaultTheme(isDefault ? "" : theme._id);
    setIsOpen(false);
  };

  const handleDelete = () => {
    setIsOpen(false);
    closeThemesSelectPopover();
    dispatch(
      openDialog({
        id: DeleteThemeDialog.dialogId,
        content: DeleteThemeDialog,
        props: { theme },
        closeOnOutsideClick: true,
      }),
    );
  };

  return (
    <Popover placement="bottom-end" open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger
        className={cn(
          "tw-flex tw-size-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-blue-50",
          isOpen && "tw-bg-blue-50",
        )}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <Icon icon="ri-more-fill" className="tw-text-blue-600" />
      </Popover.Trigger>
      <Popover.Content className="theme-item-actions-popover tw-z-[99999]">
        <div
          className="tw-w-[170px] tw-space-y-0.5 tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue/5 tw-bg-white tw-p-1 tw-shadow-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <ActionItem icon={<IconEdit />} onClick={handleRename}>
            Rename
          </ActionItem>
          <ActionItem icon={<IconStar />} onClick={handleMakeDefault}>
            {isDefault ? "Remove default" : "Set as default"}
          </ActionItem>
          <ActionItem
            icon={<IconTrash className="tw-text-red-500" />}
            onClick={handleDelete}
            destructive
          >
            Delete theme
          </ActionItem>
        </div>
      </Popover.Content>
    </Popover>
  );
};

const ActionItem = ({
  icon,
  onClick,
  destructive,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  children: React.ReactNode;
}) => (
  <div
    role="button"
    tabIndex={0}
    className={cn(
      "tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-rounded tw-px-3 tw-py-2 tw-transition-colors tw-duration-200",
      destructive ? "hover:tw-bg-[#ffeef0]" : "hover:tw-bg-blue-100/50",
    )}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick();
    }}
  >
    {icon}
    <span
      className={cn(
        "tw-font-sans tw-text-[12px]/tight tw-font-normal",
        destructive ? "tw-text-[#D43548]" : "tw-text-blue",
      )}
    >
      {children}
    </span>
  </div>
);

export default ThemeItemActions;
